import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Hand, CheckCircle2, ChevronRight, Phone, Mail } from "lucide-react";

const STATUS_COLORS = {
  unclaimed: "#A8A29E",
  claimed: "#EAB308",
  contacted: "#4A5D23",
};

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || "#A8A29E";
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider"
      style={{ backgroundColor: `${color}1A`, color }}
      data-testid={`status-${status}`}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {status}
    </span>
  );
}

function LicenseBadge({ code }) {
  if (!code) return <span className="text-xs text-stone-400 font-mono">—</span>;
  return (
    <Badge
      className="rounded-sm font-mono text-[10px] uppercase tracking-wider border bg-transparent text-[#4A6FA5] border-[#4A6FA5]/40"
    >
      {code}
    </Badge>
  );
}

function expiresSoon(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.round((d - new Date()) / 86400000);
  return days;
}

export default function ContactsTable({
  contacts,
  loading,
  currentUser,
  onClaim,
  onContacted,
  onOpen,
  emptyTitle = "No contacts found",
  emptyHint = "Try adjusting your filters or use Import Roster to load a state file.",
}) {
  if (loading) {
    return (
      <div className="border border-stone-300 bg-white">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-14 shimmer border-b border-stone-200 last:border-b-0" />
        ))}
      </div>
    );
  }

  if (!contacts.length) {
    return (
      <div
        className="border border-dashed border-stone-300 bg-white p-14 text-center"
        data-testid="contacts-empty"
      >
        <h4 className="font-display text-xl font-semibold tracking-tight">{emptyTitle}</h4>
        <p className="text-sm text-stone-500 mt-2 max-w-sm mx-auto">{emptyHint}</p>
      </div>
    );
  }

  const isAdmin = currentUser?.role === "admin";

  return (
    <div className="border border-stone-300 bg-white overflow-hidden" data-testid="contacts-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--surface)] border-b border-stone-300">
              <Th>Contact</Th>
              <Th>Company</Th>
              <Th>License</Th>
              <Th>Licensed</Th>
              <Th>Mailing</Th>
              <Th>Expires</Th>
              <Th>Status</Th>
              <Th>Assigned</Th>
              <Th className="text-right pr-5">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => {
              const locked = c.status === "contacted";
              const mine = c.assigned_to === currentUser?.id;
              const claimable = !locked && (!c.assigned_to || mine);
              const expiresDays = expiresSoon(c.license_expires);
              const expiresColor =
                expiresDays === null
                  ? "#A8A29E"
                  : expiresDays < 0
                  ? "#B91C1C"
                  : expiresDays < 30
                  ? "#B85C38"
                  : expiresDays < 90
                  ? "#EAB308"
                  : "#4A5D23";

              return (
                <tr
                  key={c.id}
                  onClick={() => onOpen(c)}
                  className={`border-b border-stone-200 last:border-b-0 hover:bg-stone-50 transition-colors cursor-pointer ${
                    locked ? "locked-stripe" : ""
                  }`}
                  data-testid={`contact-row-${c.id}`}
                >
                  <Td>
                    <div className="font-medium text-stone-900 flex items-center gap-1.5">
                      {locked && <Lock className="h-3 w-3 text-[#4A5D23]" />}
                      {c.name || "—"}
                      <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-stone-500 mt-0.5">
                      {c.email && (
                        <span className="inline-flex items-center gap-1 font-mono">
                          <Mail className="h-3 w-3" />{c.email.length > 28 ? c.email.slice(0, 28) + "…" : c.email}
                        </span>
                      )}
                      {c.phone && (
                        <span className="inline-flex items-center gap-1 font-mono">
                          <Phone className="h-3 w-3" />{c.phone}
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <span className="text-sm text-stone-700">{c.company || "—"}</span>
                    {c.county && (
                      <div className="text-[11px] text-stone-500 font-mono">{c.county}</div>
                    )}
                  </Td>
                  <Td><LicenseBadge code={c.license_code} /></Td>
                  <Td><span className="font-mono text-xs">{c.licensed_state || "—"}</span></Td>
                  <Td>
                    <span className="font-mono text-xs text-stone-700">
                      {c.mailing_state || "—"}
                      {c.mailing_city && <span className="text-stone-400"> · {c.mailing_city}</span>}
                    </span>
                  </Td>
                  <Td>
                    {c.license_expires ? (
                      <span className="font-mono text-[11px]" style={{ color: expiresColor }}>
                        {c.license_expires}
                        {expiresDays !== null && expiresDays < 90 && (
                          <span className="block text-[10px]">{expiresDays}d</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-stone-400 font-mono">—</span>
                    )}
                  </Td>
                  <Td><StatusPill status={c.status} /></Td>
                  <Td>
                    {c.assigned_to_name ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-xs"
                        data-testid={`assigned-${c.id}`}
                      >
                        <span
                          className="h-5 w-5 rounded-full grid place-items-center text-[10px] font-bold text-white"
                          style={{ backgroundColor: mine ? "#B85C38" : "#57534E" }}
                        >
                          {c.assigned_to_name?.[0]?.toUpperCase()}
                        </span>
                        <span className="text-stone-700">{mine ? "You" : c.assigned_to_name}</span>
                      </span>
                    ) : (
                      <span className="text-xs text-stone-400 font-mono">—</span>
                    )}
                  </Td>
                  <Td className="text-right pr-5">
                    <div
                      className="flex items-center justify-end gap-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {claimable && !mine && (
                        <Button
                          size="sm"
                          onClick={() => onClaim(c.id)}
                          className="h-8 rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
                          data-testid={`claim-btn-${c.id}`}
                        >
                          <Hand className="h-3.5 w-3.5 mr-1" /> Claim
                        </Button>
                      )}
                      {!locked && mine && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onContacted(c.id)}
                          className="h-8 rounded-sm border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23] hover:text-white"
                          data-testid={`contacted-btn-${c.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Contacted
                        </Button>
                      )}
                      {!locked && !mine && c.assigned_to && isAdmin && (
                        <span
                          className="text-[10px] font-mono uppercase tracking-wider text-stone-500 px-2 py-1 border border-stone-300"
                          title="Reassign to yourself from the contact panel before marking contacted"
                          data-testid={`row-claimed-lock-${c.id}`}
                        >
                          Claimed
                        </span>
                      )}
                      {locked && (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-[#4A5D23] px-2 py-1 border border-[#4A5D23]/30">
                          Locked
                        </span>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`text-left px-4 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600 font-semibold whitespace-nowrap ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}
